<template>
  <div class="step-duplicates">
    <v-container fluid class="pa-6 px-4">
      <v-row justify="center">
        <v-col cols="12" lg="11">
          <div v-if="!duplicates || duplicates.length === 0" class="text-center py-8">
            <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
            <div class="text-h6">No duplicate products found</div>
            <div class="text-body-2 grey--text">Each product identifier is unique</div>
          </div>

          <div v-else>
            <div v-if="warnDuplicates.length > 0" class="mb-6">
              <div class="d-flex align-center mb-2">
                <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
                <span class="text-h6 font-weight-medium">{{ warnDuplicates.length }} duplicate warning{{ warnDuplicates.length !== 1 ? 's' : '' }}</span>
              </div>
              <div class="text-body-2 grey--text text--darken-1 mb-4">
                These duplicate UPCs are in different categories or have unique Asset IDs, so they don't conflict. All rows will be kept.
              </div>
              <v-card outlined class="mb-3">
                <v-list dense class="py-0">
                  <v-list-item v-for="(dup, idx) in warnDuplicates" :key="dup.identifier" class="warn-list-item">
                    <v-list-item-icon class="mr-3">
                      <v-icon :color="dup.resolved ? 'success' : 'warning'" small>
                        {{ dup.resolved ? 'mdi-check-circle' : 'mdi-alert' }}
                      </v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-body-2">
                        <strong>"{{ dup.identifier }}"</strong> - {{ dup.occurrences.length }} occurrence{{ dup.occurrences.length !== 1 ? 's' : '' }} in Rows {{ formatRowList(dup.occurrences.map(o => o.rowNumber)) }}
                      </v-list-item-title>
                      <v-list-item-subtitle class="text-caption">
                        Each occurrence has a unique Asset ID.
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
                <v-divider v-if="!allWarningsSkipped"></v-divider>
                <div v-if="!allWarningsSkipped" class="d-flex justify-end pa-3">
                  <v-btn color="primary" @click="skipAllWarnings">
                    <v-icon left small>mdi-skip-next</v-icon>
                    Skip All Warnings
                  </v-btn>
                </div>
              </v-card>
            </div>

            <template v-if="blockDuplicates.length > 0">
              <div class="d-flex align-center mb-6 mt-2">
                <div class="text-h6 font-weight-medium">
                  <strong>{{ currentBlockIndex + 1 }}</strong> of <strong>{{ blockDuplicates.length }}</strong>
                  to review
                </div>
                <v-spacer></v-spacer>
                <div class="d-flex">
                  <v-chip v-for="(dup, idx) in blockDuplicates" :key="idx" small class="mx-1"
                    :class="{ 'chip-disabled': !isChipClickable(idx) }" :color="getChipColor(dup, idx)"
                    :text-color="idx === currentBlockIndex ? 'white' : undefined" @click="goToDuplicate(idx)">
                    {{ idx + 1 }}
                  </v-chip>
                </div>
              </div>

              <v-alert v-if="currentDuplicate" type="error" prominent outlined class="mb-4">
                <div class="text-h6 mb-1">This product appears {{ currentDuplicate.occurrences.length }} time{{
                  currentDuplicate.occurrences.length > 1 ? 's' : '' }} in your file</div>
                <div class="text-body-2">
                  The product <strong>"{{ currentDuplicate.identifier }}"</strong> is in
                  Rows {{ formatRowList(currentDuplicate.occurrences.map(o => o.rowNumber)) }}.
                  <template v-if="currentDuplicate.reason === 'missing-asset-id'">
                    <br>One or more rows are missing a unique Asset Detail ID.
                  </template>
                  <br><strong>Please choose which one to keep.</strong> The other{{ currentDuplicate.occurrences.length >
                    2 ? 's' : '' }} will be removed.
                </div>
              </v-alert>

              <div v-if="currentDuplicate" :key="currentBlockIndex" class="duplicate-content mb-4">
                <v-alert v-if="currentDuplicate.areIdentical" type="info" dense outlined class="mb-4">
                  These rows are exact copies. We recommend keeping the first one.
                </v-alert>

                <v-radio-group v-model="currentDuplicate.resolution" @change="onResolutionChange(currentDuplicate)"
                  class="mt-0 mb-4" hide-details>
                  <v-radio v-for="(occ, idx) in currentDuplicate.occurrences" :key="occ.rowNumber" :value="'keep-' + idx"
                    class="mb-2">
                    <template v-slot:label>
                      <span>
                        Keep the version from <strong>Row {{ occ.rowNumber }}</strong>
                        <span class="grey--text"> (remove Row {{ formatRowList(getOtherRows(currentDuplicate, idx))
                        }})</span>
                      </span>
                      <v-chip v-if="currentDuplicate.areIdentical && idx === 0" x-small color="success" text-color="white"
                        class="ml-2">
                        Recommended
                      </v-chip>
                    </template>
                  </v-radio>
                </v-radio-group>

                <div class="comparison-section">
                  <v-simple-table dense class="comparison-table">
                    <thead>
                      <tr>
                        <th class="field-header">Field</th>
                        <th v-for="(occ, occIdx) in currentDuplicate.occurrences" :key="occ.rowNumber"
                          class="row-header clickable-header"
                          :class="{ 'selected-column-header': isColumnSelected(currentDuplicate, occIdx) }"
                          @click="selectRow(currentDuplicate, occIdx)">
                          <div class="row-header-content">
                            <div class="row-header-main">
                              <span class="row-number">Row {{ occ.rowNumber }}</span>
                              <v-icon v-if="isColumnSelected(currentDuplicate, occIdx)" x-small color="success"
                                class="ml-1">
                                mdi-check-circle
                              </v-icon>
                            </div>
                            <v-btn outlined color="primary" class="click-to-keep-btn"
                              v-if="!isColumnSelected(currentDuplicate, occIdx)">
                              Click to keep
                            </v-btn>
                            <div class="selected-hint" v-else>
                              Keeping this row
                            </div>
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="field in getComparisonFields(currentDuplicate)" :key="field.name">
                        <td class="field-name-cell">{{ field.displayName }}</td>
                        <td v-for="(value, idx) in field.values" :key="idx"
                          :class="getCellClass(currentDuplicate, idx, field.isDifferent)">
                          {{ value || '(empty)' }}
                        </td>
                      </tr>
                    </tbody>
                  </v-simple-table>
                </div>
              </div>

              <div class="duplicate-action-footer">
                <div class="action-hint" v-if="!currentDuplicate.resolution">
                  <v-icon small class="mr-1">mdi-information-outline</v-icon>
                  Choose an option above, then click the button to continue
                </div>
                <v-btn color="primary" :disabled="!currentDuplicate.resolution" @click="applyAndNext" large>
                  {{ isLastBlock ? 'Continue' : 'Next' }}
                  <v-icon right>mdi-chevron-right</v-icon>
                </v-btn>
              </div>
            </template>

            <v-alert :type="allBlocksResolved ? 'success' : 'warning'" class="mt-4" outlined>
              <template v-if="allBlocksResolved && allWarningsSkipped">
                All duplicates resolved. You can continue.
              </template>
              <template v-else-if="allBlocksResolved && !allWarningsSkipped">
                All blocking duplicates resolved. Skip the warnings above to continue.
              </template>
              <template v-else>
                {{ unresolvedBlockCount }} duplicate{{ unresolvedBlockCount > 1 ? 's' : '' }} still need{{ unresolvedBlockCount === 1 ?
                  's' : '' }} your attention.
              </template>
            </v-alert>
          </div>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
export default {
  name: 'StepDuplicates',
  props: {
    duplicates: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentBlockIndex: 0
    };
  },
  computed: {
    warnDuplicates() {
      return this.duplicates.filter(d => d.severity === 'warn');
    },
    blockDuplicates() {
      return this.duplicates.filter(d => d.severity === 'block');
    },
    currentDuplicate() {
      return this.blockDuplicates[this.currentBlockIndex] || null;
    },
    allWarningsSkipped() {
      return this.warnDuplicates.length === 0 || this.warnDuplicates.every(d => d.resolved);
    },
    allBlocksResolved() {
      return this.blockDuplicates.length === 0 || this.blockDuplicates.every(d => d.resolved);
    },
    unresolvedBlockCount() {
      return this.blockDuplicates.filter(d => !d.resolved).length;
    },
    isLastBlock() {
      return this.currentBlockIndex === this.blockDuplicates.length - 1;
    }
  },
  methods: {
    skipAllWarnings() {
      this.warnDuplicates.forEach(dup => {
        dup.resolved = true;
        dup.resolution = 'skip';
      });
      this.$emit('update:duplicates', [...this.duplicates]);
      if (this.allBlocksResolved) {
        this.$nextTick(() => {
          this.$emit('all-resolved');
        });
      }
    },
    formatRowList(numbers) {
      if (!numbers || numbers.length === 0) return '';
      if (numbers.length === 1) return String(numbers[0]);
      if (numbers.length === 2) return `${numbers[0]} and ${numbers[1]}`;

      const allButLast = numbers.slice(0, -1).join(', ');
      return `${allButLast}, and ${numbers[numbers.length - 1]}`;
    },
    getOtherRows(duplicate, excludeIndex) {
      return duplicate.occurrences
        .filter((_, idx) => idx !== excludeIndex)
        .map(o => o.rowNumber);
    },
    getChipColor(duplicate, index) {
      if (index === this.currentBlockIndex) {
        return 'primary';
      }
      if (duplicate.resolved) {
        return 'success';
      }
      return undefined;
    },
    isChipClickable(index) {
      if (this.blockDuplicates[index].resolved) {
        return true;
      }
      if (index === this.currentBlockIndex) {
        return true;
      }
      const firstUnresolvedIndex = this.blockDuplicates.findIndex(d => !d.resolved);
      return index === firstUnresolvedIndex;
    },
    goToDuplicate(index) {
      if (!this.isChipClickable(index)) {
        return;
      }
      this.currentBlockIndex = index;
    },
    findNextUnresolved() {
      const nextUnresolved = this.blockDuplicates.findIndex(
        (d, idx) => idx > this.currentBlockIndex && !d.resolved
      );
      if (nextUnresolved !== -1) {
        return nextUnresolved;
      }
      return this.blockDuplicates.findIndex(
        (d, idx) => idx !== this.currentBlockIndex && !d.resolved
      );
    },
    applyAndNext() {
      if (this.currentDuplicate) {
        this.currentDuplicate.resolved = true;
        this.$emit('update:duplicates', [...this.duplicates]);

        if (this.allBlocksResolved) {
          this.$nextTick(() => {
            this.$emit('all-resolved');
          });
        } else {
          this.$nextTick(() => {
            const next = this.findNextUnresolved();
            if (next !== -1) {
              this.currentBlockIndex = next;
            }
          });
        }
      }
    },
    isColumnSelected(duplicate, columnIndex) {
      if (!duplicate.resolution) return false;

      const match = duplicate.resolution.match(/^keep-(\d+)$/);
      if (match) {
        return parseInt(match[1], 10) === columnIndex;
      }
      return false;
    },
    getCellClass(duplicate, columnIndex, isDifferent) {
      const classes = {};

      if (this.isColumnSelected(duplicate, columnIndex)) {
        classes['selected-cell'] = true;
        if (isDifferent) {
          classes['selected-different-cell'] = true;
        }
      } else if (isDifferent) {
        classes['different-cell'] = true;
      }

      return classes;
    },
    getComparisonFields(duplicate) {
      if (!duplicate.occurrences[0]) return [];

      const priorityFields = ['Name', 'Identifier', 'SectionName', 'CategoryName', 'Description'];
      const allFields = Object.keys(duplicate.occurrences[0].data);

      const attributeFields = allFields.filter(f => f.startsWith('Attribute-'));

      const otherFields = allFields.filter(f =>
        !priorityFields.includes(f) && !f.startsWith('Attribute-')
      );

      const orderedFields = [
        ...priorityFields.filter(f => allFields.includes(f)),
        ...otherFields,
        ...attributeFields
      ];

      return orderedFields.map(fieldName => {
        const values = duplicate.occurrences.map(occurrence => {
          const val = occurrence.data[fieldName];
          if (val === undefined || val === null || val === '') return '';
          const str = String(val);
          return str.length > 60 ? str.substring(0, 57) + '...' : str;
        });

        const uniqueValues = new Set(values.map(v => v || ''));

        return {
          name: fieldName,
          displayName: this.formatFieldName(fieldName),
          values: values,
          isDifferent: uniqueValues.size > 1
        };
      });
    },
    formatFieldName(name) {
      if (name.startsWith('Attribute-')) {
        return name.substring(10);
      }
      return name.replace(/([A-Z])/g, ' $1').trim();
    },
    onResolutionChange(duplicate) {
      duplicate.resolved = duplicate.resolution !== null;
      this.$emit('update:duplicates', [...this.duplicates]);
    },
    selectRow(duplicate, index) {
      duplicate.resolution = 'keep-' + index;
      this.onResolutionChange(duplicate);
    }
  }
};
</script>

<style scoped>
.warn-list-item::before {
  background-color: transparent !important;
}

.comparison-section {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  overflow: hidden;
}

.comparison-table {
  border-top: none;
}

.comparison-table th {
  background: #f5f5f5 !important;
  font-size: 12px !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.comparison-table .field-header {
  width: 160px;
  min-width: 140px;
}

.comparison-table .row-header {
  color: #1976d2;
  padding: 12px 16px !important;
  vertical-align: middle;
}

.comparison-table .clickable-header {
  cursor: pointer;
  user-select: none;
  transition: all 0.2s ease;
}

.comparison-table .clickable-header:hover {
  background: #e3f2fd !important;
}

.comparison-table .clickable-header:hover .click-to-keep-btn {
  opacity: 1;
}

.comparison-table .selected-column-header {
  background: #c8e6c9 !important;
  color: #2e7d32 !important;
}

.row-header-content {
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.row-header-main {
  display: flex;
  align-items: center;
}

.row-number {
  font-weight: 600;
}

.click-to-keep-btn {
  min-height: 36px;
  padding: 0 16px !important;
  font-weight: 500;
  text-transform: none;
  letter-spacing: normal;
  opacity: 0.9;
  transition: opacity 0.2s ease;
}

.selected-hint {
  font-size: 14px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: normal;
  color: #2e7d32;
}

.comparison-table .field-name-cell {
  font-weight: 500;
  color: #616161;
  font-size: 13px;
}

.comparison-table td {
  font-size: 13px !important;
  max-width: 300px;
  word-break: break-word;
}

.selected-cell {
  background-color: #e8f5e9 !important;
}

.selected-different-cell {
  background-color: #fff3e0 !important;
  border-left: 3px solid #ff9800 !important;
}

.different-cell {
  background-color: #fff3e0 !important;
  border-left: 3px solid #ff9800 !important;
}

.chip-disabled {
  opacity: 0.4;
  cursor: not-allowed !important;
  pointer-events: auto;
}

.duplicate-action-footer {
  position: sticky;
  bottom: 0;
  background: white;
  padding: 16px 0;
  margin-top: 16px;
  border-top: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 16px;
  z-index: 10;
}

.action-hint {
  color: #757575;
  font-size: 13px;
  display: flex;
  align-items: center;
}
</style>
